import { useState, type CSSProperties } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { useQuery } from "@tanstack/react-query";

import { plans as plansApi } from "@/api/client";
import { PageHead } from "@/components/PageHead";

/** The PLAN × ROLE matrix, rendered so it can be READ rather than inferred.
 *
 *  D-77 made Orbit authoritative for what its own features mean, and that
 *  authority is a constant in shared/lib/fabric_auth/plans.py. A constant
 *  nobody can see is a spec nobody checks — so it gets a page.
 *
 *  NOT rendered through DataTable (U2), which is the law for DATA tables. This
 *  is a static 19-row spec, and it needs three things DataTable's flat
 *  `DataCol[]` cannot express: a two-tier header (each plan spanning its two
 *  role columns), group separator rows, and a WRAPPING first column where
 *  DataTable deliberately ellipsizes. Teaching colSpan, row grouping and wrap
 *  to the component that carries the measured live-tail perf fixes, for one
 *  table that never re-renders, is the worse trade. What IS reused is its
 *  technique — `table-layout: fixed` + explicit colgroup + the same sticky
 *  tokens — so the fixes are not silently re-lost here. Backlog:
 *  docs/status/UI_COMMONALIZATION.md.
 *
 *  READ-ONLY on purpose. The matrix is code, so its audit trail is git: author,
 *  timestamp, diff, review, and the reason in the commit message. Making these
 *  boxes clickable would move the source of truth into a database row and lose
 *  every one of those. The ticks show what IS, not what you can set.
 */
/** px. The Capability column is capped so a long `detail` wraps inside it
 *  rather than widening the column and shoving the plan columns off-screen. */
const CAP_W = 300;

/** The role the collapsed view keeps. Server-side this is
 *  `shared/lib/fabric_auth/roles.COMPANY_ADMIN`; the console only ever sees it
 *  as a string in the payload, so it is named once here rather than spelled
 *  into three separate comparisons. */
const ADMIN = "company_admin";
const COL_W = 96;

/** Pinned to the left edge so the capability stays readable while the six plan
 *  columns scroll under it — without it you are looking at a row of ticks with
 *  no idea which capability they belong to. Needs an OPAQUE background (the
 *  scrolled columns pass underneath) and a divider so the seam is visible.
 *  Same tokens DataTable's `stickyCell` uses, so both themes stay correct. */
const stickyCap: CSSProperties = {
  position: "sticky", left: 0, zIndex: 1,
  background: "var(--surface)",
  borderRight: "1px solid var(--line2)",
  textAlign: "left",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};

/** Same pin for the two header rows. `.tbl th` paints itself `--well`, so the
 *  body's `--surface` would leave the corner cell a white square against a grey
 *  header strip. zIndex above the body cells for the same reason they need one. */
const stickyCapHead: CSSProperties = {
  ...stickyCap, background: "var(--well)", zIndex: 2,
};

export function PlansMatrixPage() {
  /** Collapsed by default: six columns of ticks is more than anyone reads at
   *  once, and the question people arrive with is "what does this PLAN give
   *  me", which the admin column answers. Company user is the narrower
   *  follow-up question, so it is one click away rather than always on screen.
   *  Deliberately NOT persisted — the default view is the legible one, and a
   *  sticky preference would quietly hand the next visitor the cluttered grid. */
  const [showUsers, setShowUsers] = useState(false);
  const q = useQuery({ queryKey: ["plans"], queryFn: plansApi.matrix });
  const d = q.data;

  if (!d) {
    return (
      <div className="mpage on">
        <PageHead eyebrow="Plans · Plan × Role" title="What each plan includes"
          desc="Loading…" />
      </div>
    );
  }

  const groups = Array.from(new Set(d.capabilities.map((c) => c.group)));
  const roleLabel: Record<string, string> = {
    [ADMIN]: "Company admin",
    company_user: "Company user",
  };
  // Collapsed shows the admin column only. `|| d.roles.slice(0, 1)` matters:
  // ADMIN is a string this file asserts about a payload it does not own, and a
  // filter that matches nothing would render a table with ZERO plan columns —
  // a blank grid that looks like a data problem rather than a renaming one.
  const collapsed = d.roles.filter((r) => r === ADMIN);
  const shown = showUsers || !collapsed.length ? d.roles : collapsed;
  const span = d.plans.length * shown.length;

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Plans · Plan × Role"
        title="What each plan includes"
        desc={
          <>
            Effective permission is <strong>both</strong>: the plan must include
            the capability <em>and</em> the role must be allowed it. Aphelion
            Energy is an ordinary company on this grid — it runs on Pro +, so its
            admins and users see exactly what any other Pro + customer's admins
            and users see, scoped to their own data.
          </>
        }
      />

      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap", marginBottom: 14,
      }}>
      <div className="cap">
        {d.your_plan === null ? (
          <>
            You are a <strong>super admin</strong> — every capability below, plus
            the internal ones no plan sells, across every company. No plan
            applies to you.
          </>
        ) : (
          <>
            Your company is on{" "}
            <strong>{d.plans.find((p) => p.id === d.your_plan)?.label ?? d.your_plan}</strong>
            {" · "}you are <strong>{roleLabel[d.your_role] ?? d.your_role}</strong>
          </>
        )}
      </div>
        <button className="btn ghost sm" aria-expanded={showUsers}
          onClick={() => setShowUsers((v) => !v)}
          title={showUsers
            ? "Hide the company-user columns"
            : "Show what a company USER gets on each plan, beside the admin"}>
          {showUsers ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {showUsers ? "Hide company user" : "Show company user"}
        </button>
      </div>

      <div className="tblwrap">
        {/* fixed layout + colgroup: the Capability column keeps its declared
            width instead of being stretched by its longest sentence, which is
            what let the grid push the plan columns off-screen. */}
        <table className="tbl" style={{
          tableLayout: "fixed", width: "100%",
          minWidth: CAP_W + span * COL_W,
        }}>
          <colgroup>
            <col style={{ width: CAP_W }} />
            {d.plans.flatMap((p) =>
              shown.map((r) => <col key={`${p.id}:${r}`} style={{ width: COL_W }} />))}
          </colgroup>
          <thead>
            <tr>
              {/* rowSpan only when there IS a second header row — an unmatched
                  rowSpan={2} over a single row silently eats the first body row. */}
              <th style={stickyCapHead} rowSpan={showUsers ? 2 : 1}>Capability</th>
              {d.plans.map((p) => (
                <th key={p.id} colSpan={shown.length} style={{ textAlign: "center" }}>
                  {p.label}
                </th>
              ))}
            </tr>
            {showUsers && (
              <tr>
                {d.plans.flatMap((p) =>
                  shown.map((r) => (
                    <th key={`${p.id}:${r}`} className="cap"
                        style={{ textAlign: "center", fontWeight: 600 }}>
                      {r === ADMIN ? "Admin" : "User"}
                    </th>
                  )),
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {/* A NUMBER, not a tick. This row is the argument that split
                entitlement from permission (D-77): retention is 90/180/365
                days, and a permission map holds booleans — you cannot express
                it without inventing a fake `retention_90` permission. Rendered
                as a value so the page shows that, rather than hiding it in the
                header where it read as a caption. */}
            <tr>
              <td colSpan={1 + span} style={{ paddingTop: 16 }}>
                <span className="section-label flush"
                      style={{ position: "sticky", left: 0 }}>Limits</span>
              </td>
            </tr>
            <tr>
              <td style={stickyCap}>
                <div style={{ fontWeight: 600 }}>Data retention</div>
                <div className="cap">
                  How far back history goes. Applies to the company, so both
                  roles get the same window.
                </div>
              </td>
              {d.plans.map((p) => (
                <td key={p.id} colSpan={shown.length}
                    style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                  <span style={{ fontWeight: 700, color: "var(--t-bright)" }}>
                    {p.limits.retention_days}
                  </span>
                  <span className="cap"> days</span>
                </td>
              ))}
            </tr>

            {groups.map((g) => (
              <Group
                key={g}
                name={g}
                caps={d.capabilities.filter((c) => c.group === g)}
                plans={d.plans}
                roles={shown}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Group({
  name, caps, plans, roles,
}: {
  name: string;
  caps: { id: number; label: string; detail: string; grid: Record<string, Record<string, boolean>> }[];
  plans: { id: string; label: string }[];
  roles: string[];
}) {
  return (
    <>
      <tr>
        {/* The group label rides a sticky SPAN inside a full-width cell: the
            cell spans every column, so it cannot itself be pinned to the left
            edge without breaking the span. */}
        <td colSpan={1 + plans.length * roles.length} style={{ paddingTop: 16 }}>
          <span className="section-label flush"
                style={{ position: "sticky", left: 0 }}>{name}</span>
        </td>
      </tr>
      {caps.map((c) => (
        <tr key={c.id}>
          <td style={stickyCap}>
            <div style={{ fontWeight: 600 }}>{c.label}</div>
            <div className="cap">{c.detail}</div>
          </td>
          {plans.flatMap((p) =>
            roles.map((r) => (
              <td key={`${p.id}:${r}`} style={{ textAlign: "center" }}>
                <Tick on={c.grid[p.id]?.[r] ?? false} />
              </td>
            )),
          )}
        </tr>
      ))}
    </>
  );
}

/** A checkbox that is deliberately not an input: it reports, it does not set.
 *
 *  The state is carried by `role="img"` + `aria-label`, NOT by a visually
 *  hidden text node. An earlier version shipped `<span className="sr-only">`
 *  — a class this codebase has never defined — so "included" / "not included"
 *  rendered as visible text in all 114 cells. A label on the element itself
 *  cannot fall back to being visible, because there is no text node to reveal.
 */
function Tick({ on }: { on: boolean }) {
  return (
    <span
      role="img"
      aria-label={on ? "included" : "not included"}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 17, height: 17, borderRadius: 4, fontSize: 12, fontWeight: 800,
        border: `1px solid ${on ? "var(--ok)" : "var(--line2)"}`,
        background: on ? "var(--ok)" : "transparent",
        color: on ? "#fff" : "transparent",
      }}
    >
      ✓
    </span>
  );
}
